import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { catalogApi } from '../api/catalog';
import { contentApi } from '../api/content';
import { accountApi } from '../api/account';
import { ApiError } from '../api/client';
import type { CategoryDto, MenuItemDto } from '../api/types';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { SearchAutocomplete } from './SearchAutocomplete';
import { Spinner } from './ui';
import { initAnalytics, trackPageView } from '../lib/analytics';

/* ------------------------------------------------------------------ *
 *  Vitrin kabugu: ust menu, arama, sepet ozeti, hesap menusu, alt bilgi.
 * ------------------------------------------------------------------ */

export function Layout() {
  const location = useLocation();
  const queryClient = useQueryClient();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [slowBoot, setSlowBoot] = useState(false);

  const isHome = location.pathname === '/';

  const settingsQuery = useQuery({
    queryKey: ['content', 'settings'],
    queryFn: () => contentApi.publicSettings(),
    staleTime: 5 * 60_000,
  });

  const categoriesQuery = useQuery({
    queryKey: ['catalog', 'category-tree'],
    queryFn: () => catalogApi.categoryTree(),
    staleTime: 5 * 60_000,
  });

  const homeBannersQuery = useQuery({
    queryKey: ['content', 'banners', 'home-hero'],
    queryFn: () => contentApi.banners('home-hero'),
    staleTime: 30_000,
    enabled: isHome,
  });

  const homeFeaturedQuery = useQuery({
    queryKey: ['products', 'featured-home'],
    queryFn: () => catalogApi.searchProducts({ pageSize: 8, featuredOnly: true }),
    enabled: isHome,
  });

  const settings = settingsQuery.data;

  const isInitialLoading =
    settingsQuery.isPending ||
    categoriesQuery.isPending ||
    (isHome && (homeBannersQuery.isPending || homeFeaturedQuery.isPending));

  const isInitialError =
    !isInitialLoading &&
    (settingsQuery.isError ||
      categoriesQuery.isError ||
      (isHome && homeFeaturedQuery.isError));

  const isRetrying =
    settingsQuery.isFetching ||
    categoriesQuery.isFetching ||
    (isHome && (homeBannersQuery.isFetching || homeFeaturedQuery.isFetching));

  useEffect(() => {
    if (!isInitialLoading) {
      setSlowBoot(false);
      return;
    }
    const timer = setTimeout(() => setSlowBoot(true), 2500);
    return () => clearTimeout(timer);
  }, [isInitialLoading]);

  // Analytics başlatma (GA4, GTM, Meta Pixel)
  useEffect(() => {
    if (settings) {
      initAnalytics(settings);
    }
  }, [settings]);

  // Sayfa degisince acik menu kapansin ve sayfa goruntuleme olayi gonderilsin.
  useEffect(() => {
    setMobileOpen(false);
    window.scrollTo({ top: 0 });
    trackPageView(window.location.href);
  }, [location.pathname, location.search]);

  if (isInitialLoading) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-gradient-to-b from-brand-50/40 via-white to-white px-6 text-center">
        <div className="relative flex items-center justify-center">
          <div className="h-20 w-20 animate-ping rounded-full bg-brand-200/50 duration-1000" />
          <div className="absolute flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-600 to-brand-800 text-2xl text-amber-300 shadow-lg shadow-brand-900/20">
            🌴
          </div>
        </div>
        <div className="mt-6 flex items-center gap-2.5 text-brand-900">
          <Spinner className="h-5 w-5 text-brand-600" />
          <span className="text-base font-bold tracking-tight">Mağaza Yükleniyor…</span>
        </div>
        <p className="mt-2 max-w-xs text-xs text-ink-500 leading-relaxed">
          {slowBoot
            ? 'Sunucu bağlantısı hazırlanıyor, lütfen birkaç saniye bekleyin…'
            : 'Ürünler ve mağaza bilgileri getiriliyor…'}
        </p>
      </div>
    );
  }

  if (isInitialError) {
    const rawError =
      settingsQuery.error ?? categoriesQuery.error ?? homeFeaturedQuery.error;
    const errorDetail =
      rawError instanceof ApiError && rawError.errors.length > 0
        ? rawError.errors[0]
        : null;

    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-ink-50/50 px-6 py-12 text-center">
        <div className="w-full max-w-md rounded-3xl border border-ink-200 bg-white p-8 shadow-xl">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-red-50 text-red-600 border border-red-100">
            <svg viewBox="0 0 24 24" className="h-7 w-7" fill="none" stroke="currentColor" strokeWidth="1.75">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" strokeLinecap="round" />
              <line x1="12" y1="16" x2="12.01" y2="16" strokeLinecap="round" />
            </svg>
          </div>
          <h1 className="mt-5 text-xl font-bold text-ink-900">Bir Hata Oluştu</h1>
          <p className="mt-2 text-sm text-ink-600 leading-relaxed">
            Sunucuya şu anda ulaşılamıyor veya veriler yüklenirken geçici bir sorun oluştu. Lütfen birkaç saniye bekleyip tekrar deneyin.
          </p>
          {errorDetail ? (
            <p className="mt-3 rounded-xl bg-red-50/80 border border-red-100 px-3 py-2 text-xs text-red-700">
              {errorDetail}
            </p>
          ) : null}
          <button
            type="button"
            disabled={isRetrying}
            onClick={() => {
              void queryClient.refetchQueries();
            }}
            className="btn-primary mt-6 w-full justify-center rounded-xl py-3 text-sm font-semibold shadow-md"
          >
            {isRetrying ? (
              <>
                <Spinner className="h-4 w-4" />
                <span>Yeniden Bağlanılıyor…</span>
              </>
            ) : (
              <span>Tekrar Dene</span>
            )}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col animate-in fade-in duration-300">
      <AnnouncementBar />
      <Header mobileOpen={mobileOpen} onToggleMobile={() => setMobileOpen((open) => !open)} />
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}

function AnnouncementBar() {
  const { data } = useQuery({
    queryKey: ['content', 'settings'],
    queryFn: () => contentApi.publicSettings(),
    staleTime: 5 * 60_000,
  });

  if (!data) {
    return null;
  }

  const isEnabled = data['store.announcement_enabled'] !== 'false';
  if (!isEnabled) {
    return null;
  }

  const rawTexts = data['store.announcement_texts'];
  const items = (rawTexts ? rawTexts.split('\n') : [])
    .map((t) => t.trim())
    .filter(Boolean);

  if (items.length === 0) {
    return null;
  }

  // Kesintisiz ve boşluksuz sonsuz döngü için seti en az 4 öğeye tamamla
  const trackItems = [...items];
  while (trackItems.length < 4) {
    trackItems.push(...items);
  }

  return (
    <div className="relative overflow-hidden border-b border-brand-800/20 bg-gradient-to-r from-brand-900 via-brand-800 to-brand-900 py-2 text-xs font-medium text-brand-100 shadow-inner">
      <div className="animate-marquee flex items-center whitespace-nowrap">
        {/* Set 1 */}
        <div className="flex shrink-0 items-center gap-10 px-5">
          {trackItems.map((item, idx) => (
            <span key={`a-${idx}`} className="flex items-center gap-3">
              <span>{item}</span>
              <span className="text-amber-400/80">✦</span>
            </span>
          ))}
        </div>
        {/* Set 2 (Sonsuz kesintisiz akış için kopyası) */}
        <div className="flex shrink-0 items-center gap-10 px-5" aria-hidden="true">
          {trackItems.map((item, idx) => (
            <span key={`b-${idx}`} className="flex items-center gap-3">
              <span>{item}</span>
              <span className="text-amber-400/80">✦</span>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function Header({
  mobileOpen,
  onToggleMobile,
}: {
  mobileOpen: boolean;
  onToggleMobile: () => void;
}) {
  const { user, isAuthenticated, logout } = useAuth();
  const { itemCount } = useCart();

  const { data: settings } = useQuery({
    queryKey: ['content', 'settings'],
    queryFn: () => contentApi.publicSettings(),
    staleTime: 5 * 60_000,
  });

  const { data: categories } = useQuery({
    queryKey: ['catalog', 'category-tree'],
    queryFn: () => catalogApi.categoryTree(),
    staleTime: 5 * 60_000,
  });

  const { data: menu } = useQuery({
    queryKey: ['content', 'menu', 'main'],
    queryFn: () => contentApi.menu('main'),
    staleTime: 30_000,
  });

  const { data: mobileMenu } = useQuery({
    queryKey: ['content', 'menu', 'mobile'],
    queryFn: () => contentApi.menu('mobile'),
    staleTime: 30_000,
  });

  const { data: wishlistIds } = useQuery({
    queryKey: ['account', 'wishlist-ids'],
    queryFn: () => accountApi.wishlistIds(),
    enabled: isAuthenticated,
    staleTime: 30_000,
  });
  const wishlistCount = wishlistIds?.length ?? 0;

  return (
    <header className="sticky top-0 z-40 border-b border-ink-200 bg-white/95 backdrop-blur">
      {/* Üst Satır: Logo, Arama, Hesap ve Sepet */}
      <div className="mx-auto flex h-16 w-full max-w-[1600px] items-center justify-between gap-4 px-4 sm:px-6 lg:px-10">
        <div className="flex items-center gap-3">
          <button
            type="button"
            className="-ml-2 rounded-lg p-2 text-ink-600 lg:hidden"
            onClick={onToggleMobile}
            aria-expanded={mobileOpen}
            aria-label="Menüyü aç"
          >
            <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="1.75">
              <path d="M4 7h16M4 12h16M4 17h16" strokeLinecap="round" />
            </svg>
          </button>

          <Link to="/" className="shrink-0 flex items-center gap-2.5">
            {settings?.['store.logo_url'] ? (
              <img
                src={settings['store.logo_url']}
                alt={settings['store.name'] || 'Hurmahane'}
                className="h-9 max-h-9 max-w-[190px] object-contain"
              />
            ) : (
              <div className="flex items-center gap-2">
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-brand-600 to-brand-800 text-lg text-amber-300 shadow-sm shadow-brand-900/20">
                  🌴
                </span>
                <div className="flex flex-col">
                  <span className="text-xl font-black tracking-tight text-brand-950 leading-none">
                    {settings?.['store.name'] || 'Hurmahane'}
                  </span>
                  <span className="text-[10px] font-semibold tracking-widest text-brand-700 uppercase">
                    Hac & Hurma Pazarı
                  </span>
                </div>
              </div>
            )}
          </Link>
        </div>

        <div className="hidden flex-1 max-w-xl mx-auto md:block">
          <SearchAutocomplete placeholder="Ürün, marka, kategori, barkod ara..." />
        </div>

        <div className="flex items-center gap-1.5">
          {isAuthenticated ? (
            <AccountMenu name={user?.firstName ?? user?.email ?? 'Hesabım'} onLogout={logout} />
          ) : (
            <Link to="/giris" className="btn-ghost btn-sm hidden sm:inline-flex">
              Giriş yap
            </Link>
          )}

          <Link
            to="/hesabim/favoriler"
            className="relative rounded-lg p-2 text-ink-600 transition hover:bg-ink-100 hover:text-ink-900"
            aria-label={`Favorilerim: ${wishlistCount} ürün`}
            title="Favorilerim"
          >
            <svg
              viewBox="0 0 24 24"
              className={`h-5 w-5 ${wishlistCount > 0 ? 'text-rose-500 fill-rose-500' : ''}`}
              fill="none"
              stroke="currentColor"
              strokeWidth="1.75"
            >
              <path
                d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {wishlistCount > 0 ? (
              <span className="absolute -right-0.5 -top-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-semibold leading-none text-white">
                {wishlistCount}
              </span>
            ) : null}
          </Link>

          <Link
            to="/sepet"
            className="relative rounded-lg p-2 text-ink-600 transition hover:bg-ink-100 hover:text-ink-900"
            aria-label={`Sepet: ${itemCount} ürün`}
          >
            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.75">
              <path d="M3 4h2l2.4 10.4a2 2 0 0 0 2 1.6h7.2a2 2 0 0 0 2-1.6L20 7H6" strokeLinecap="round" strokeLinejoin="round" />
              <circle cx="10" cy="19" r="1.4" />
              <circle cx="17" cy="19" r="1.4" />
            </svg>
            {itemCount > 0 ? (
              <span className="absolute -right-0.5 -top-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-brand-600 px-1 text-[10px] font-semibold leading-none text-white">
                {itemCount}
              </span>
            ) : null}
          </Link>
        </div>
      </div>

      {/* Alt Satır: Kategori Menüsü (Masaüstü) */}
      <div className="hidden border-t border-ink-100 bg-ink-50/40 lg:block">
        <div className="mx-auto flex w-full max-w-[1600px] items-center px-4 sm:px-6 lg:px-10">
          <nav className="flex w-full items-center gap-1 py-1.5" aria-label="Ana menü">
            <NavLink to="/urunler" className={navClass}>
              Tüm ürünler
            </NavLink>
            {(categories ?? []).map((category) => (
              <CategoryDropdownItem key={category.id} category={category} />
            ))}
            {(menu ?? []).map((item) => (
              <MenuLink key={item.id} item={item} />
            ))}
          </nav>
        </div>
      </div>

      {mobileOpen ? (
        <div className="border-t border-ink-200 bg-white lg:hidden">
          <div className="container-page space-y-1 py-3">
            <div className="pb-3 md:hidden">
              <SearchAutocomplete onCloseMobile={onToggleMobile} placeholder="Ürün, marka, kategori ara..." />
            </div>
            <NavLink to="/urunler" className={mobileNavClass} onClick={onToggleMobile}>
              Tüm ürünler
            </NavLink>
            {(categories ?? []).map((category) => (
              <MobileCategory key={category.id} category={category} />
            ))}

            {/* Dinamik Menü Öğeleri */}
            {(mobileMenu && mobileMenu.length > 0 ? mobileMenu : (menu ?? [])).map((item) => (
              <MobileMenuItem key={item.id} item={item} onCloseMobile={onToggleMobile} />
            ))}

            <NavLink to="/blog" className={mobileNavClass} onClick={onToggleMobile}>
              Blog
            </NavLink>
            <NavLink to="/sss" className={mobileNavClass} onClick={onToggleMobile}>
              Sıkça sorulanlar
            </NavLink>
            <NavLink to="/iletisim" className={mobileNavClass} onClick={onToggleMobile}>
              İletişim
            </NavLink>
            <NavLink to="/siparis-takip" className={mobileNavClass} onClick={onToggleMobile}>
              Sipariş takibi
            </NavLink>
            {!isAuthenticated ? (
              <NavLink to="/giris" className={mobileNavClass} onClick={onToggleMobile}>
                Giriş yap
              </NavLink>
            ) : null}
          </div>
        </div>
      ) : null}
    </header>
  );
}

function navClass({ isActive }: { isActive: boolean }) {
  return `whitespace-nowrap rounded-lg px-3 py-1.5 text-sm font-medium transition ${
    isActive ? 'bg-brand-600 text-white shadow-sm' : 'text-ink-700 hover:bg-white hover:text-brand-700'
  }`;
}

function SubCategoryFlyoutItem({ category }: { category: CategoryDto }) {
  const hasChildren = Boolean(category.children && category.children.length > 0);

  return (
    <div className="relative group/sub">
      <Link
        to={`/kategori/${category.slug}`}
        className="flex items-center justify-between gap-3 rounded-lg px-3 py-2 text-xs font-medium text-ink-700 transition hover:bg-brand-50 hover:text-brand-700"
      >
        <span>{category.name}</span>
        <span className="flex items-center gap-1.5">
          {typeof category.productCount === 'number' && category.productCount > 0 ? (
            <span className="text-[10px] text-ink-400 font-mono">({category.productCount})</span>
          ) : null}
          {hasChildren ? (
            <svg
              className="h-3.5 w-3.5 text-ink-400 transition-transform group-hover/sub:translate-x-0.5 group-hover/sub:text-brand-600"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          ) : null}
        </span>
      </Link>

      {/* Alt kategorinin de alt kategorisi varsa sağa doğru yan menü (flyout) açılır */}
      {hasChildren ? (
        <div className="absolute left-full top-0 hidden pl-1.5 group-hover/sub:block z-50 min-w-[220px]">
          <div className="rounded-2xl border border-ink-200 bg-white p-2 shadow-xl ring-1 ring-black/5 animate-in fade-in slide-in-from-left-1">
            <Link
              to={`/kategori/${category.slug}`}
              className="block rounded-lg px-3 py-1.5 text-[11px] font-semibold text-brand-700 hover:bg-brand-50"
            >
              Tüm {category.name} →
            </Link>
            <div className="my-1 border-t border-ink-100" />
            <div className="space-y-0.5">
              {category.children!.map((grandchild) => (
                <SubCategoryFlyoutItem key={grandchild.id} category={grandchild} />
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function CategoryDropdownItem({ category, isSub = false }: { category: CategoryDto; isSub?: boolean }) {
  const hasChildren = Boolean(category.children && category.children.length > 0);

  if (isSub) {
    return <SubCategoryFlyoutItem category={category} />;
  }

  return (
    <div className="relative group">
      <NavLink
        to={`/kategori/${category.slug}`}
        className={({ isActive }) =>
          `flex items-center gap-1 whitespace-nowrap rounded-lg px-3 py-1.5 text-sm font-medium transition ${
            isActive ? 'bg-brand-600 text-white shadow-sm' : 'text-ink-700 hover:bg-white hover:text-brand-700'
          }`
        }
      >
        <span>{category.name}</span>
        {hasChildren ? (
          <svg
            className="h-3.5 w-3.5 shrink-0 opacity-70 transition-transform duration-200 group-hover:rotate-180"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        ) : null}
      </NavLink>

      {hasChildren ? (
        <div className="absolute left-0 top-full hidden pt-1.5 group-hover:block z-50 min-w-[240px]">
          <div className="rounded-2xl border border-ink-200 bg-white p-2 shadow-xl ring-1 ring-black/5 animate-in fade-in slide-in-from-top-1">
            <Link
              to={`/kategori/${category.slug}`}
              className="block rounded-lg px-3 py-2 text-xs font-semibold text-brand-700 hover:bg-brand-50"
            >
              Tüm {category.name} Ürünleri →
            </Link>
            <div className="my-1 border-t border-ink-100" />
            <div className="space-y-0.5">
              {category.children!.map((child) => (
                <SubCategoryFlyoutItem key={child.id} category={child} />
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function mobileNavClass({ isActive }: { isActive: boolean }) {
  return `block rounded-lg px-3 py-2 text-sm ${
    isActive ? 'bg-ink-100 font-medium text-ink-900' : 'text-ink-700'
  }`;
}

function MobileCategory({ category }: { category: CategoryDto }) {
  return (
    <div>
      <NavLink to={`/kategori/${category.slug}`} className={mobileNavClass}>
        {category.name}
      </NavLink>
      {category.children?.length ? (
        <div className="ml-3 border-l border-ink-100 pl-3 space-y-0.5">
          {category.children.map((child) => (
            <MobileCategory key={child.id} category={child} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** Menu ogesi ic baglantiysa router, dis baglantiysa normal <a> kullanir; alt ogeleri varsa acilir menu gosterir. */
function MenuLink({ item }: { item: MenuItemDto }) {
  const url = item.url ?? '#';
  const isExternal = /^https?:\/\//i.test(url);
  const hasChildren = Boolean(item.children && item.children.length > 0);

  if (!hasChildren) {
    if (isExternal) {
      return (
        <a
          href={url}
          target={item.openInNewTab ? '_blank' : undefined}
          rel={item.openInNewTab ? 'noreferrer noopener' : undefined}
          className="flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm text-ink-600 transition hover:bg-ink-50 hover:text-ink-900"
        >
          <span>{item.label}</span>
          {item.badgeText ? (
            <span className="rounded bg-brand-50 px-1.5 py-0.5 text-[10px] font-semibold text-brand-700">
              {item.badgeText}
            </span>
          ) : null}
        </a>
      );
    }

    return (
      <NavLink to={url} className={navClass}>
        <span className="flex items-center gap-1.5">
          <span>{item.label}</span>
          {item.badgeText ? (
            <span className="rounded bg-brand-50 px-1.5 py-0.5 text-[10px] font-semibold text-brand-700">
              {item.badgeText}
            </span>
          ) : null}
        </span>
      </NavLink>
    );
  }

  // Alt menüsü olan öğe için açılır menü
  return (
    <div className="relative group">
      {isExternal ? (
        <a
          href={url}
          target={item.openInNewTab ? '_blank' : undefined}
          rel={item.openInNewTab ? 'noreferrer noopener' : undefined}
          className="flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium text-ink-600 transition hover:bg-ink-50 hover:text-ink-900"
        >
          <span>{item.label}</span>
          {item.badgeText ? (
            <span className="rounded bg-brand-50 px-1.5 py-0.5 text-[10px] font-semibold text-brand-700">
              {item.badgeText}
            </span>
          ) : null}
          <svg
            className="h-3.5 w-3.5 text-ink-400 transition-transform duration-200 group-hover:rotate-180 group-hover:text-ink-700"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </a>
      ) : (
        <NavLink
          to={url}
          className={({ isActive }) =>
            `flex items-center gap-1 rounded-lg px-3 py-2 text-sm transition ${
              isActive ? 'bg-ink-100 font-medium text-ink-900' : 'text-ink-600 hover:bg-ink-50 hover:text-ink-900'
            }`
          }
        >
          <span>{item.label}</span>
          {item.badgeText ? (
            <span className="rounded bg-brand-50 px-1.5 py-0.5 text-[10px] font-semibold text-brand-700">
              {item.badgeText}
            </span>
          ) : null}
          <svg
            className="h-3.5 w-3.5 text-ink-400 transition-transform duration-200 group-hover:rotate-180 group-hover:text-ink-700"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </NavLink>
      )}

      <div className="absolute left-0 top-full hidden pt-1.5 group-hover:block z-50 min-w-[200px]">
        <div className="rounded-2xl border border-ink-200 bg-white p-2 shadow-xl ring-1 ring-black/5 animate-in fade-in slide-in-from-top-1">
          {item.children.map((child) => {
            const childUrl = child.url ?? '#';
            const childIsExternal = /^https?:\/\//i.test(childUrl);
            return childIsExternal ? (
              <a
                key={child.id}
                href={childUrl}
                target={child.openInNewTab ? '_blank' : undefined}
                rel={child.openInNewTab ? 'noreferrer noopener' : undefined}
                className="flex items-center justify-between rounded-lg px-3 py-2 text-xs font-medium text-ink-700 transition hover:bg-brand-50 hover:text-brand-700"
              >
                <span>{child.label}</span>
                {child.badgeText ? (
                  <span className="rounded bg-brand-50 px-1.5 py-0.5 text-[9px] font-semibold text-brand-700">
                    {child.badgeText}
                  </span>
                ) : null}
              </a>
            ) : (
              <Link
                key={child.id}
                to={childUrl}
                className="flex items-center justify-between rounded-lg px-3 py-2 text-xs font-medium text-ink-700 transition hover:bg-brand-50 hover:text-brand-700"
              >
                <span>{child.label}</span>
                {child.badgeText ? (
                  <span className="rounded bg-brand-50 px-1.5 py-0.5 text-[9px] font-semibold text-brand-700">
                    {child.badgeText}
                  </span>
                ) : null}
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function MobileMenuItem({ item, onCloseMobile }: { item: MenuItemDto; onCloseMobile: () => void }) {
  const url = item.url ?? '#';
  const isExternal = /^https?:\/\//i.test(url);
  const hasChildren = Boolean(item.children && item.children.length > 0);

  return (
    <div>
      {isExternal ? (
        <a
          href={url}
          target={item.openInNewTab ? '_blank' : undefined}
          rel={item.openInNewTab ? 'noreferrer noopener' : undefined}
          onClick={onCloseMobile}
          className="flex items-center justify-between rounded-lg px-3 py-2.5 text-sm text-ink-700 hover:bg-ink-100"
        >
          <span>{item.label}</span>
          {item.badgeText ? (
            <span className="rounded bg-brand-50 px-1.5 py-0.5 text-[10px] font-semibold text-brand-700">
              {item.badgeText}
            </span>
          ) : null}
        </a>
      ) : (
        <NavLink
          to={url}
          onClick={onCloseMobile}
          className={({ isActive }) =>
            `flex items-center justify-between rounded-lg px-3 py-2.5 text-sm ${
              isActive ? 'bg-ink-100 font-medium text-ink-900' : 'text-ink-700 hover:bg-ink-50'
            }`
          }
        >
          <span>{item.label}</span>
          {item.badgeText ? (
            <span className="rounded bg-brand-50 px-1.5 py-0.5 text-[10px] font-semibold text-brand-700">
              {item.badgeText}
            </span>
          ) : null}
        </NavLink>
      )}

      {hasChildren ? (
        <div className="ml-3 border-l border-ink-100 pl-3 space-y-1">
          {item.children.map((child) => {
            const childUrl = child.url ?? '#';
            const childIsExternal = /^https?:\/\//i.test(childUrl);
            return childIsExternal ? (
              <a
                key={child.id}
                href={childUrl}
                target={child.openInNewTab ? '_blank' : undefined}
                rel={child.openInNewTab ? 'noreferrer noopener' : undefined}
                onClick={onCloseMobile}
                className="flex items-center justify-between rounded-lg px-3 py-2 text-xs text-ink-600 hover:bg-ink-100"
              >
                <span>{child.label}</span>
                {child.badgeText ? (
                  <span className="rounded bg-brand-50 px-1 py-0.5 text-[9px] font-semibold text-brand-700">
                    {child.badgeText}
                  </span>
                ) : null}
              </a>
            ) : (
              <NavLink
                key={child.id}
                to={childUrl}
                onClick={onCloseMobile}
                className={({ isActive }) =>
                  `flex items-center justify-between rounded-lg px-3 py-2 text-xs ${
                    isActive ? 'bg-ink-100 font-medium text-ink-900' : 'text-ink-600 hover:bg-ink-50'
                  }`
                }
              >
                <span>{child.label}</span>
                {child.badgeText ? (
                  <span className="rounded bg-brand-50 px-1 py-0.5 text-[9px] font-semibold text-brand-700">
                    {child.badgeText}
                  </span>
                ) : null}
              </NavLink>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function AccountMenu({ name, onLogout }: { name: string; onLogout: () => Promise<void> }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;

    const onClick = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    };

    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const links = [
    { to: '/hesabim', label: 'Hesap özeti' },
    { to: '/hesabim/kuponlarim', label: 'Kuponlarım' },
    { to: '/hesabim/siparisler', label: 'Siparişlerim' },
    { to: '/hesabim/iadeler', label: 'İadelerim' },
    { to: '/hesabim/favoriler', label: 'Favorilerim' },
    { to: '/hesabim/adresler', label: 'Adreslerim' },
    { to: '/hesabim/guvenlik', label: 'Güvenlik' },
  ];

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="flex items-center gap-1.5 rounded-lg px-2.5 py-2 text-sm text-ink-700 transition hover:bg-ink-100"
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.75">
          <circle cx="12" cy="8.5" r="3.5" />
          <path d="M4.5 20a7.5 7.5 0 0 1 15 0" strokeLinecap="round" />
        </svg>
        <span className="hidden max-w-[7rem] truncate sm:inline">{name}</span>
      </button>

      {open ? (
        <div
          role="menu"
          className="absolute right-0 mt-1 w-52 overflow-hidden rounded-xl border border-ink-200 bg-white py-1 shadow-pop"
        >
          {links.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              role="menuitem"
              className="block px-4 py-2 text-sm text-ink-700 hover:bg-ink-50"
              onClick={() => setOpen(false)}
            >
              {link.label}
            </Link>
          ))}
          <div className="my-1 border-t border-ink-100" />
          <button
            type="button"
            role="menuitem"
            className="block w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50"
            onClick={() => {
              setOpen(false);
              void onLogout();
            }}
          >
            Çıkış yap
          </button>
        </div>
      ) : null}
    </div>
  );
}

function Footer() {
  const { data: settings } = useQuery({
    queryKey: ['content', 'settings'],
    queryFn: () => contentApi.publicSettings(),
    staleTime: 5 * 60_000,
  });

  const { data: pages } = useQuery({
    queryKey: ['content', 'pages'],
    queryFn: () => contentApi.pages(),
    staleTime: 10 * 60_000,
  });

  const { data: footerMenu } = useQuery({
    queryKey: ['content', 'menu', 'footer'],
    queryFn: () => contentApi.menu('footer'),
    staleTime: 30_000,
  });

  const storeName = settings?.['store.name'] || 'Hurmahane';
  const storeDesc =
    settings?.['store.tagline'] ||
    settings?.['store.description'] ||
    '';
  const storeEmail = settings?.['store.email'] || settings?.['store.supportEmail'] || '';
  const storePhone = settings?.['store.phone'] || settings?.['store.supportPhone'] || '';
  const storeAddress = settings?.['store.address'] || settings?.['store.companyAddress'] || '';
  const logoUrl = settings?.['store.logo_url'];

  return (
    <footer className="mt-16 border-t border-ink-200 bg-ink-50">
      <div className="container-page grid gap-8 py-12 sm:grid-cols-2 lg:grid-cols-4">
        <div>
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={storeName}
              className="h-8 max-h-8 max-w-[160px] object-contain mb-3"
            />
          ) : (
            <div className="flex items-center gap-2 mb-3">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-700 text-base text-amber-300">
                🌴
              </span>
              <p className="text-lg font-bold text-ink-900">{storeName}</p>
            </div>
          )}
          {storeDesc ? (
            <p className="mt-2 text-sm text-ink-600 leading-relaxed">{storeDesc}</p>
          ) : null}

          {/* İletişim Bilgileri */}
          {(storePhone || storeEmail || storeAddress) ? (
            <ul className="mt-4 space-y-2 text-xs text-ink-600">
              {storePhone ? (
                <li className="flex items-center gap-2">
                  <svg className="h-4 w-4 shrink-0 text-brand-700" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
                  </svg>
                  <a href={`tel:${storePhone.replace(/\s+/g, '')}`} className="font-medium hover:text-brand-700 transition-colors">
                    {storePhone}
                  </a>
                </li>
              ) : null}
              {storeEmail ? (
                <li className="flex items-center gap-2">
                  <svg className="h-4 w-4 shrink-0 text-brand-700" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                    <rect width="20" height="16" x="2" y="4" rx="2" />
                    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                  </svg>
                  <a href={`mailto:${storeEmail}`} className="hover:text-brand-700 transition-colors">
                    {storeEmail}
                  </a>
                </li>
              ) : null}
              {storeAddress ? (
                <li className="flex items-start gap-2">
                  <svg className="h-4 w-4 shrink-0 text-brand-700 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                  <span>{storeAddress}</span>
                </li>
              ) : null}
            </ul>
          ) : null}

          {/* Sosyal Medya */}
          <div className="mt-4 flex flex-wrap items-center gap-2.5">
            {settings?.['social.instagram'] && (
              <a
                href={settings['social.instagram']}
                target="_blank"
                rel="noopener noreferrer"
                className="flex h-8 w-8 items-center justify-center rounded-lg bg-white border border-ink-200 text-ink-600 hover:text-pink-600 hover:border-pink-300 transition shadow-sm"
                title="Instagram"
                aria-label="Instagram"
              >
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect width="20" height="20" x="2" y="2" rx="5" ry="5" />
                  <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                  <line x1="17.5" x2="17.51" y1="6.5" y2="6.5" />
                </svg>
              </a>
            )}
            {settings?.['social.facebook'] && (
              <a
                href={settings['social.facebook']}
                target="_blank"
                rel="noopener noreferrer"
                className="flex h-8 w-8 items-center justify-center rounded-lg bg-white border border-ink-200 text-ink-600 hover:text-blue-600 hover:border-blue-300 transition shadow-sm"
                title="Facebook"
                aria-label="Facebook"
              >
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
                </svg>
              </a>
            )}
            {settings?.['social.x'] && (
              <a
                href={settings['social.x']}
                target="_blank"
                rel="noopener noreferrer"
                className="flex h-8 w-8 items-center justify-center rounded-lg bg-white border border-ink-200 text-ink-600 hover:text-ink-900 hover:border-ink-400 transition shadow-sm"
                title="X (Twitter)"
                aria-label="X"
              >
                <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
              </a>
            )}
            {settings?.['social.youtube'] && (
              <a
                href={settings['social.youtube']}
                target="_blank"
                rel="noopener noreferrer"
                className="flex h-8 w-8 items-center justify-center rounded-lg bg-white border border-ink-200 text-ink-600 hover:text-red-600 hover:border-red-300 transition shadow-sm"
                title="YouTube"
                aria-label="YouTube"
              >
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M2.5 17a24.12 24.12 0 0 1 0-10 2 2 0 0 1 1.4-1.4 49.56 49.56 0 0 1 16.2 0A2 2 0 0 1 21.5 7a24.12 24.12 0 0 1 0 10 2 2 0 0 1-1.4 1.4 49.55 49.55 0 0 1-16.2 0A2 2 0 0 1 2.5 17" />
                  <path d="m10 15 5-3-5-3z" fill="currentColor" />
                </svg>
              </a>
            )}
            {settings?.['social.whatsapp'] && (
              <a
                href={`https://wa.me/${settings['social.whatsapp'].replace(/[^0-9]/g, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex h-8 w-8 items-center justify-center rounded-lg bg-white border border-ink-200 text-ink-600 hover:text-brand-600 hover:border-brand-300 transition shadow-sm"
                title="WhatsApp"
                aria-label="WhatsApp"
              >
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
                </svg>
              </a>
            )}
          </div>
        </div>

        <nav aria-label="Kurumsal">
          <p className="mb-3 text-sm font-semibold text-ink-800">Kurumsal</p>
          <ul className="space-y-2 text-sm text-ink-600">
            {(pages ?? [])
              .filter((page) => page.showInFooter)
              .map((page) => (
                <li key={page.id}>
                  <Link to={`/sayfa/${page.slug}`} className="hover:text-ink-900 transition-colors">
                    {page.title}
                  </Link>
                </li>
              ))}
            {(footerMenu ?? []).map((item) => {
              const url = item.url ?? '#';
              const isExternal = /^https?:\/\//i.test(url);
              return (
                <li key={item.id}>
                  {isExternal ? (
                    <a
                      href={url}
                      target={item.openInNewTab ? '_blank' : undefined}
                      rel={item.openInNewTab ? 'noreferrer noopener' : undefined}
                      className="hover:text-ink-900 transition-colors"
                    >
                      {item.label}
                    </a>
                  ) : (
                    <Link to={url} className="hover:text-ink-900 transition-colors">
                      {item.label}
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>
        </nav>

        <nav aria-label="Yardım">
          <p className="mb-3 text-sm font-semibold text-ink-800">Yardım</p>
          <ul className="space-y-2 text-sm text-ink-600">
            <li>
              <Link to="/siparis-takip" className="hover:text-ink-900">
                Sipariş takibi
              </Link>
            </li>
            <li>
              <Link to="/sss" className="hover:text-ink-900">
                Sıkça sorulan sorular
              </Link>
            </li>
            <li>
              <Link to="/iletisim" className="hover:text-ink-900">
                İletişim
              </Link>
            </li>
            <li>
              <Link to="/blog" className="hover:text-ink-900">
                Blog
              </Link>
            </li>
          </ul>
        </nav>

        <NewsletterBox />
      </div>

      {/* Guvenli Odeme ve Kargo Rozetleri */}
      <div className="border-t border-ink-100 bg-ink-50/50 py-6">
        <div className="container-page flex flex-wrap items-center justify-between gap-4 text-xs text-ink-600">
          <div className="flex flex-wrap items-center gap-6">
            <span className="font-semibold text-ink-800">Güvenli Alışveriş:</span>
            <span className="flex items-center gap-1.5 font-medium">
              <span className="text-brand-600">🔒</span> 256-Bit SSL Sertifikası
            </span>
            <span className="flex items-center gap-1.5 font-medium">
              <span className="text-brand-600">🛡️</span> 3D Secure Doğrulama
            </span>
            <span className="flex items-center gap-1.5 font-medium">
              <span className="text-indigo-600">🚚</span> Hızlı & Sigortalı Gönderi
            </span>
          </div>

          <div className="flex items-center gap-2">
            <span className="rounded border border-ink-200 bg-white px-2 py-1 font-mono text-[10px] font-bold text-ink-700">
              TROY
            </span>
            <span className="rounded border border-ink-200 bg-white px-2 py-1 font-mono text-[10px] font-bold text-blue-700">
              VISA
            </span>
            <span className="rounded border border-ink-200 bg-white px-2 py-1 font-mono text-[10px] font-bold text-orange-600">
              Mastercard
            </span>
            <span className="rounded border border-ink-200 bg-white px-2 py-1 text-[10px] font-medium text-ink-600">
              Havale / EFT
            </span>
          </div>
        </div>
      </div>

      <div className="border-t border-ink-200 py-5">
        <p className="container-page text-xs text-ink-500">
          © {new Date().getFullYear()} {storeName}. Tüm hakları saklıdır.
        </p>
      </div>
    </footer>
  );
}

function NewsletterBox() {
  const [email, setEmail] = useState('');
  const [state, setState] = useState<'idle' | 'sending' | 'done' | 'error'>('idle');
  const [message, setMessage] = useState<string | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setState('sending');
    try {
      const result = await contentApi.subscribeNewsletter({ email, source: 'footer' });
      setMessage(result ?? 'Onay e-postası gönderildi.');
      setState('done');
      setEmail('');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Kayıt tamamlanamadı.');
      setState('error');
    }
  };

  return (
    <div>
      <p className="mb-3 text-sm font-semibold text-ink-800">Bülten</p>
      <p className="mb-3 text-sm text-ink-500">
        Yeni ürünler ve kampanyalardan ilk siz haberdar olun.
      </p>
      <form onSubmit={submit} className="flex gap-2">
        <label className="sr-only" htmlFor="newsletter-email">
          E-posta adresi
        </label>
        <input
          id="newsletter-email"
          type="email"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder="ornek@eposta.com"
          className="field py-2 text-sm"
        />
        <button type="submit" className="btn-primary btn-sm shrink-0" disabled={state === 'sending'}>
          Kaydol
        </button>
      </form>
      {message ? (
        <p className={`mt-2 text-xs ${state === 'error' ? 'text-red-600' : 'text-emerald-700'}`}>
          {message}
        </p>
      ) : null}
    </div>
  );
}
